import * as React from "react";
import { add, Duration } from "date-fns";

// An enum with all the types of actions to use in our reducer
export enum RunCreateVolumeSnapshotReducerActionType {
  LOCATION = "LOCATION",
  INDEX = "INDEX",
  NAME = "NAME",
  DATE = "DATE",
  ADD_TAG = "ADD_TAG",
  REMOVE_TAG = "REMOVE_TAG",
  CHANGE_TAG = "CHANGE_TAG",
}

// An interface for our actions
interface RunCreateVolumeSnapshotReducerAction {
  type: RunCreateVolumeSnapshotReducerActionType;
  payload: any;
}

// An interface for our state
export interface CreateVolumeSnapshotReducerState {
  location: string;
  volumeIndex: string;
  snapshotName: string;
  snapshotExpirationDate: string | null;
  snapshotTags: { index: number; name: string; value: string }[];
}

// Our reducer function that uses a switch statement to handle our actions
function runCreateVolumeSnapshot(
  state: CreateVolumeSnapshotReducerState,
  action: RunCreateVolumeSnapshotReducerAction,
) {
  const { type, payload } = action;
  switch (type) {
    case RunCreateVolumeSnapshotReducerActionType.LOCATION:
      return {
        ...state,
        location: payload,
      };
    case RunCreateVolumeSnapshotReducerActionType.INDEX:
      return {
        ...state,
        volumeIndex: payload,
      };
    case RunCreateVolumeSnapshotReducerActionType.NAME:
      return {
        ...state,
        snapshotName: payload,
      };
    case RunCreateVolumeSnapshotReducerActionType.DATE:
      return {
        ...state,
        snapshotExpirationDate: payload,
      };
    case RunCreateVolumeSnapshotReducerActionType.ADD_TAG:
      return {
        ...state,
        snapshotTags: [...state.snapshotTags, { index: state.snapshotTags.length, name: "", value: "" }],
      };
    case RunCreateVolumeSnapshotReducerActionType.REMOVE_TAG:
      return {
        ...state,
        snapshotTags: state.snapshotTags.filter((e) => e.index !== payload),
      };
    case RunCreateVolumeSnapshotReducerActionType.CHANGE_TAG:
      return {
        ...state,
        snapshotTags: state.snapshotTags.map((tag) => {
          if (tag.index === payload.index) {
            return payload;
          }
          return tag;
        }),
      };
    default:
      return state;
  }
}

export function useRunCreateVolumeSnapshotReducer(
  initialLocation: string | undefined,
  initialVolumeIndex: string | undefined,
) {
  const initialState: CreateVolumeSnapshotReducerState = {
    location: initialLocation || "",
    volumeIndex: initialVolumeIndex || "",
    snapshotName: "",
    snapshotExpirationDate: null,
    snapshotTags: [],
  };
  return React.useReducer(runCreateVolumeSnapshot, initialState);
}

export function getExpirationDateFromRetentionDuration(retentionDuration?: string): string | null {
  if (!retentionDuration) {
    return null;
  }

  const amount = Number(retentionDuration.slice(0, retentionDuration.length - 1));
  const unit = retentionDuration.slice(retentionDuration.length - 1);
  const now = new Date();

  // Map retention duration units to date-fns duration properties
  let duration: Duration = {};
  switch (unit) {
    case "s":
      duration = { seconds: amount };
      break;
    case "m":
      duration = { minutes: amount };
      break;
    case "h":
      duration = { hours: amount };
      break;
    case "d":
      duration = { days: amount };
      break;
    case "w":
      duration = { weeks: amount };
      break;
    case "M":
      duration = { months: amount };
      break;
    case "y":
      duration = { years: amount };
      break;
    default:
      return null;
  }

  return add(now, duration).toISOString();
}
